import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper, LinearProgress } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  distribution: {
    padding: theme.spacing(2),
    height: '100%',
  },
  metrics: {
    padding: theme.spacing(2),
    height: '100%',
  },
  barContainer: {
    marginBottom: theme.spacing(2),
  },
  barLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
  },
  progressBar: {
    height: 20,
    borderRadius: 10,
  },
  metricCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
}));

const MultipleChoiceVisualization = ({ data, metrics }) => {
  // Diagnostic logging for debugging MC breakdown issues
  console.log('[MultipleChoiceVisualization] props:', { data, metrics });
  if (data && Array.isArray(data.options)) {
    console.log('[MultipleChoiceVisualization] options:', data.options);
  } else {
    console.warn('[MultipleChoiceVisualization] options missing or not array:', data?.options);
  }
  if (metrics) {
    console.log('[MultipleChoiceVisualization] metrics:', metrics);
  }
  const classes = useStyles();

  const renderDistribution = () => {
    const totalResponses = data.options?.reduce((sum, option) => sum + option.count, 0) || 0;

    return (
      <Paper className={classes.distribution}>
        <Typography variant="h6" gutterBottom>
          Response Distribution
        </Typography>
        {data.options?.map((option, index) => {
          const percentage = totalResponses > 0 ? (option.count / totalResponses) * 100 : 0;
          return (
            <Box key={option.id} className={classes.barContainer}>
              <Box className={classes.barLabel}>
                <Typography variant="body2">{option.label}</Typography>
                <Typography variant="body2">{percentage.toFixed(1)}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={percentage}
                className={classes.progressBar}
                style={{
                  backgroundColor: `rgba(25, 118, 210, 0.${index + 1})`,
                }}
              />
            </Box>
          );
        })}
      </Paper>
    );
  };

  const renderMetrics = () => (
    <Paper className={classes.metrics}>
      <Typography variant="h6" gutterBottom>
        Engagement Metrics
      </Typography>
      <Box className={classes.metricCard}>
        <Typography variant="subtitle2">Total Responses</Typography>
        <Typography variant="h4">{metrics?.totalResponses || 0}</Typography>
      </Box>
      <Box className={classes.metricCard}>
        <Typography variant="subtitle2">Response Rate</Typography>
        <Typography variant="h4">{metrics?.responseRate || 0}%</Typography>
      </Box>
      <Box className={classes.metricCard}>
        <Typography variant="subtitle2">Most Selected Option</Typography>
        <Typography variant="h4">
          {data.options?.reduce((max, option) => 
            option.count > (max?.count || 0) ? option : max,
            { count: 0 }
          )?.label || 'N/A'}
        </Typography>
      </Box>
    </Paper>
  );

  return (
    <Box className={classes.root}>
      {renderDistribution()}
      {renderMetrics()}
    </Box>
  );
};

MultipleChoiceVisualization.propTypes = {
  data: PropTypes.shape({
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        label: PropTypes.string,
        count: PropTypes.number,
      })
    ),
  }),
  metrics: PropTypes.shape({
    totalResponses: PropTypes.number,
    responseRate: PropTypes.number,
  }),
};

export default MultipleChoiceVisualization; 